/**
 * The lazy data the reliability engines read, and the one async loader that
 * fetches and unpacks all of it.
 *
 * Every engine in this folder is a pure function of a context built from
 * {@link ReliabilityInputs} (or a narrower `Pick` of it). Scenes that already
 * hold the domains from `useOemData()` build the context themselves and call
 * the pure function; code outside React (the assistant, the check script)
 * calls {@link loadReliabilityInputs} and gets the same object from the same
 * module-level fetch cache.
 *
 * | Field | Domain | Key |
 * |---|---|---|
 * | `shiftDaily` | `'telemetry'` | `shift_daily` (columnar) |
 * | `eventsDaily` | `'telemetry'` | `events_daily` (columnar) |
 * | `dailyMetric` | `'telemetry'` | `daily_metric` (columnar) |
 * | `workOrders` | `'work-orders'` | `wos` (columnar) |
 * | `parts` / `inventory` | `'parts'` | `parts`, `inventory` |
 * | `live` | `'telemetry-live'` | `live` |
 * | `rmsHealth` | `'rms'` | `rms_health` |
 *
 * Engine layer (layer 2 of the 4-layer architecture).
 */

import type { CataloguePart, InventoryLine } from '../../schema/part.ts';
import type { RmsHealthRegistry } from '../../schema/telemetry.ts';
import type {
  DailyMetricRow,
  EventDailyRow,
  LiveReplayRegistry,
  ShiftDailyRow,
} from '../../schema/telemetry.ts';
import type { WorkOrder } from '../../schema/work-order.ts';
import { unpackTable } from '../columnar.ts';
import { loadOemDomain } from '../useOemData.ts';

/** Everything the reliability engines read from the lazy domains. */
export interface ReliabilityInputs {
  readonly shiftDaily: readonly ShiftDailyRow[];
  readonly eventsDaily: readonly EventDailyRow[];
  readonly dailyMetric: readonly DailyMetricRow[];
  readonly workOrders: readonly WorkOrder[];
  readonly parts: readonly CataloguePart[];
  readonly inventory: readonly InventoryLine[];
  readonly live: LiveReplayRegistry;
  readonly rmsHealth: RmsHealthRegistry;
}

/** Fetch (or join the in-flight fetch of) the five domains and unpack them. */
export async function loadReliabilityInputs(): Promise<ReliabilityInputs> {
  const [telemetry, wos, parts, live, rms] = await Promise.all([
    loadOemDomain('telemetry'),
    loadOemDomain('work-orders'),
    loadOemDomain('parts'),
    loadOemDomain('telemetry-live'),
    loadOemDomain('rms'),
  ]);
  return {
    shiftDaily: unpackTable<ShiftDailyRow>(telemetry.shift_daily),
    eventsDaily: unpackTable<EventDailyRow>(telemetry.events_daily),
    dailyMetric: unpackTable<DailyMetricRow>(telemetry.daily_metric),
    workOrders: unpackTable<WorkOrder>(wos.wos),
    parts: parts.parts,
    inventory: parts.inventory,
    live: live.live,
    rmsHealth: rms.rms_health,
  };
}
